import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { OUTCOMES, outcomeOf, parseIssueRef, timeAgo } from '../../lib/et.js';
import { css, html, nothing } from '../../lib/lit.js';

function formatDate(iso) {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

// One exploratory-testing item with everything recorded against it: results
// (each with the text the item had when it was tested), notes, and linked
// GitHub issues — plus recording a result, adding a note, and filing or linking
// an issue. An archived item still opens here, read-only, so its history stays
// reachable from issues that point at it.
class ETItemView extends BaseComponent {
    static properties = {
        itemId: { type: String, attribute: 'item-id' },
        /** @type {object|null} */
        _item: { state: true },
        _loading: { state: true },
        _error: { state: true },
        _busy: { state: true },
        _actionError: { state: true },
        _outcome: { state: true },
        _resultNote: { state: true },
        _build: { state: true },
        _noteBody: { state: true },
        _issueRepo: { state: true },
        _issueTitle: { state: true },
        _issueBody: { state: true },
        _issueRef: { state: true },
    };

    static styles = css`
        :host {
            display: block;
        }
        .wrap {
            max-width: 56rem;
            margin: 0 auto;
            padding: 2rem 1rem 4rem;
        }
        .history-note {
            white-space: pre-wrap;
        }
        .was-text {
            font-size: 0.8rem;
        }
    `;

    constructor() {
        super();
        this.itemId = '';
        this._item = null;
        this._loading = true;
        this._error = '';
        this._busy = false;
        this._actionError = '';
        this._outcome = 'pass';
        this._resultNote = '';
        this._build = '';
        this._noteBody = '';
        this._issueRepo = '';
        this._issueTitle = '';
        this._issueBody = '';
        this._issueRef = '';
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._error = '';
        try {
            this._item = await api.get(`/et/items/${this.itemId}`);
            if (!this._issueRepo) this._issueRepo = this._item.plan.defaultRepo;
        } catch (error) {
            this._error = error.status === 404 ? 'No such item.' : error.message;
        } finally {
            this._loading = false;
        }
    }

    async _act(change) {
        this._busy = true;
        this._actionError = '';
        try {
            await change();
            await this._load();
        } catch (error) {
            this._actionError = error.message;
        } finally {
            this._busy = false;
        }
    }

    render() {
        if (this._loading) return html`<div class="wrap"><p class="text-muted">Loading…</p></div>`;
        if (this._error) {
            return html`<div class="wrap">
                <div class="alert alert-danger" role="alert">${this._error}</div>
                <a href="/testing">All plans</a>
            </div>`;
        }

        const item = this._item;
        return html`
            <div class="wrap">
                <a class="small" href="/testing/plans/${item.plan.id}">
                    <i class="bi bi-arrow-left"></i> ${item.plan.name} › ${item.section.name}
                </a>
                <h1 class="h5 mt-2">${item.text}</h1>
                ${
                    item.archived
                        ? html`<div class="alert alert-secondary py-2" role="status">This item is archived; its history is kept.</div>`
                        : nothing
                }
                ${
                    this._actionError
                        ? html`<div class="alert alert-danger" role="alert">${this._actionError}</div>`
                        : nothing
                }
                ${item.archived ? nothing : this._renderRecord()}
                ${this._renderIssues()} ${this._renderHistory()}
            </div>
        `;
    }

    _renderRecord() {
        return html`
            <form class="card card-body mb-4"
                @submit=${(e) => {
                    e.preventDefault();
                    this._act(async () => {
                        await api.post(`/et/items/${this.itemId}/results`, {
                            outcome: this._outcome,
                            note: this._resultNote.trim(),
                            build: this._build.trim(),
                        });
                        this._resultNote = '';
                    });
                }}>
                <div class="d-flex flex-wrap gap-2 mb-2">
                    <div class="btn-group" role="group" aria-label="Outcome">
                        ${OUTCOMES.map(
                            (outcome) => html`<button type="button"
                                class="btn btn-sm ${this._outcome === outcome.value ? outcome.button.replace('-outline', '') : outcome.button}"
                                @click=${() => {
                                    this._outcome = outcome.value;
                                }}>${outcome.label}</button>`,
                        )}
                    </div>
                    <input class="form-control form-control-sm w-auto" placeholder="Build, e.g. v0.31.2" aria-label="Build"
                        .value=${this._build}
                        @input=${(e) => {
                            this._build = e.target.value;
                        }} />
                </div>
                <textarea class="form-control form-control-sm mb-2" rows="2" aria-label="Note"
                    placeholder=${this._outcome === 'pass' ? 'Note (optional)' : 'What happened?'}
                    .value=${this._resultNote}
                    @input=${(e) => {
                        this._resultNote = e.target.value;
                    }}></textarea>
                <div><button class="btn btn-sm btn-primary" type="submit" ?disabled=${this._busy}>Record result</button></div>
            </form>
        `;
    }

    _renderIssues() {
        const item = this._item;
        return html`
            <h2 class="h6" id="issue">Issues</h2>
            ${
                item.issues.length === 0
                    ? html`<p class="text-muted small">None linked.</p>`
                    : html`<ul class="list-group mb-3">
                          ${item.issues.map(
                              (
                                  issue,
                              ) => html`<li class="list-group-item d-flex align-items-center gap-2">
                                  <span class="badge ${issue.state === 'open' ? 'text-bg-success' : 'text-bg-secondary'}">${issue.state}</span>
                                  <a href=${issue.url} target="_blank" rel="noopener">${issue.repo}#${issue.number}</a>
                                  <span class="text-truncate">${issue.title}</span>
                                  ${
                                      item.archived
                                          ? nothing
                                          : html`<button class="btn btn-sm btn-link text-danger ms-auto" title="Unlink" ?disabled=${this._busy}
                                                @click=${() => this._act(() => api.delete(`/et/issue-links/${issue.id}`))}>Unlink</button>`
                                  }
                              </li>`,
                          )}
                      </ul>`
            }
            ${item.archived ? nothing : this._renderIssueForms()}
        `;
    }

    _renderIssueForms() {
        const ref = parseIssueRef(this._issueRef, this._item.plan.defaultRepo);
        return html`
            <div class="row g-3 mb-4">
                <form class="col-12 col-lg-7"
                    @submit=${(e) => {
                        e.preventDefault();
                        this._act(async () => {
                            await api.post(`/et/items/${this.itemId}/issues`, {
                                repo: this._issueRepo.trim(),
                                title: this._issueTitle.trim(),
                                body: this._issueBody.trim(),
                            });
                            this._issueTitle = '';
                            this._issueBody = '';
                        });
                    }}>
                    <div class="card card-body">
                        <div class="small fw-semibold">File a Bug</div>
                        <div class="form-text mt-0 mb-2">
                            Filed as you, through the GitHub account connected in your profile.
                        </div>
                        <input class="form-control form-control-sm mb-2" aria-label="Repo" required
                            .value=${this._issueRepo}
                            @input=${(e) => {
                                this._issueRepo = e.target.value;
                            }} />
                        <input class="form-control form-control-sm mb-2" aria-label="Title" maxlength="256"
                            placeholder="Title — defaults to the item"
                            .value=${this._issueTitle}
                            @input=${(e) => {
                                this._issueTitle = e.target.value;
                            }} />
                        <textarea class="form-control form-control-sm mb-2" rows="3" aria-label="Body"
                            placeholder="Body — defaults to the plan, item and latest result's note"
                            .value=${this._issueBody}
                            @input=${(e) => {
                                this._issueBody = e.target.value;
                            }}></textarea>
                        <div><button class="btn btn-sm btn-primary" type="submit" ?disabled=${this._busy}>File issue</button></div>
                    </div>
                </form>
                <form class="col-12 col-lg-5"
                    @submit=${(e) => {
                        e.preventDefault();
                        this._act(async () => {
                            await api.post(`/et/items/${this.itemId}/issue-links`, ref);
                            this._issueRef = '';
                        });
                    }}>
                    <div class="card card-body">
                        <div class="small fw-semibold mb-2">Link an existing issue</div>
                        <input class="form-control form-control-sm mb-2" aria-label="Issue"
                            placeholder="#123, repo#123 or a GitHub URL"
                            .value=${this._issueRef}
                            @input=${(e) => {
                                this._issueRef = e.target.value;
                            }} />
                        <div><button class="btn btn-sm btn-outline-primary" type="submit" ?disabled=${!ref || this._busy}>Link</button></div>
                    </div>
                </form>
            </div>
        `;
    }

    _renderHistory() {
        const item = this._item;
        const now = Date.now();
        // Results and notes, interleaved newest first.
        const entries = [
            ...item.results.map((result) => ({
                kind: 'result',
                at: result.createdAt,
                value: result,
            })),
            ...item.notes.map((note) => ({ kind: 'note', at: note.createdAt, value: note })),
        ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

        return html`
            <h2 class="h6">History</h2>
            ${
                item.archived
                    ? nothing
                    : html`<form class="d-flex gap-2 mb-3"
                          @submit=${(e) => {
                              e.preventDefault();
                              this._act(async () => {
                                  await api.post(`/et/items/${this.itemId}/notes`, {
                                      body: this._noteBody.trim(),
                                  });
                                  this._noteBody = '';
                              });
                          }}>
                          <input class="form-control form-control-sm" required placeholder="Add a note" aria-label="Note"
                              .value=${this._noteBody}
                              @input=${(e) => {
                                  this._noteBody = e.target.value;
                              }} />
                          <button class="btn btn-sm btn-outline-primary" type="submit" ?disabled=${this._busy}>Add</button>
                      </form>`
            }
            ${entries.length === 0 ? html`<p class="text-muted small">Never tested.</p>` : nothing}
            <ul class="list-group">
                ${entries.map((entry) => html`<li class="list-group-item">${this._renderEntry(entry, now)}</li>`)}
            </ul>
        `;
    }

    _renderEntry(entry, now) {
        const { value } = entry;
        const who = html`${value.by.name}
            ${value.by.kind === 'agent' ? html`<span class="badge text-bg-light border"><i class="bi bi-robot"></i> agent</span>` : nothing}`;
        const when = html`<span class="text-muted" title=${formatDate(entry.at)}>${timeAgo(entry.at, now)}</span>`;

        if (entry.kind === 'note') {
            return html`
                <div class="small"><i class="bi bi-chat-left-text"></i> ${who} · ${when}</div>
                <div class="history-note">${value.body}</div>
            `;
        }
        const outcome = outcomeOf(value.outcome);
        return html`
            <div class="small">
                <span class="badge ${outcome?.badge ?? 'text-bg-light'}">${outcome?.label ?? value.outcome}</span>
                ${who} · ${when} ${value.build ? html`· <code>${value.build}</code>` : nothing}
            </div>
            ${
                value.itemText !== this._item.text
                    ? html`<div class="text-muted was-text">Tested as: “${value.itemText}”</div>`
                    : nothing
            }
            ${value.note ? html`<div class="history-note">${value.note}</div>` : nothing}
        `;
    }
}

customElements.define('d3-et-item-view', ETItemView);
